import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';

import { HasRoleDirective } from '@directive/has-permission.directive';
import { SideNavigationMenuType } from '@type/side-navigation-menu.type';
import { UserProfileType } from '@type/user.type';
import { filter, map } from 'rxjs/operators';

import { AppIconComponent } from '@components/@fec/app-icon.component';
import { ButtonComponent } from '@components/@fec/button.component';
import {
  DropdownMenuComponent,
  DropdownMenuItemType,
} from '@components/@fec/dropdown-menu.component';
import { AvatarUserType, UserAvatarComponent } from '@components/@fec/user-avatar.component';
import { AnnouncementBellComponent } from '@components/announcement-bell/announcement-bell.component';
import { ProjectNavigatorDropdownComponent } from '@components/project-navigator-dropdown/project-navigator-dropdown.component';

import { AuthenticationService } from '@services/api/authentication.service';
import { ApiService } from '@services/helper/api-wrapper.service';
import { ThemeService } from '@services/helper/theme.service';
import { ToastService } from '@services/helper/toast.service';
import { TokenService } from '@services/helper/token.service';
import { UserPermissionService } from '@services/helper/user-permission.service';

import { ALL_PERMISSIONS } from '@models/permission.model';
import { TopMenuNavigationList } from '@models/top-menu-navigation.model';

/**
 * Application header component.
 * Renders navigation menu, user avatar, and action buttons.
 */
@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AppIconComponent,
    DropdownMenuComponent,
    UserAvatarComponent,
    RouterLink,
    RouterLinkActive,
    ButtonComponent,
    CommonModule,
    ProjectNavigatorDropdownComponent,
    AnnouncementBellComponent,
    HasRoleDirective,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  host: {
    '(window:resize)': 'onResize()',
    '(window:scroll)': 'onScroll()',
  },
})
export class HeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  protected readonly userPermissionService = inject(UserPermissionService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly tokenService = inject(TokenService);
  protected readonly themeService = inject(ThemeService);
  private readonly toastService = inject(ToastService);
  // private readonly cd = inject(ChangeDetectorRef);

  protected readonly isSticky = signal<boolean>(false);
  protected readonly menuItemCounts = signal<{
    user: number;
    request: number;
    reservations: number;
  }>({
    request: 0,
    reservations: 0,
    user: 0,
  });

  /**
   * TanStack query for /api/me — fetches authenticated user profile.
   * Runs automatically when component initialize (header is always visible).
   */
  protected readonly loggedInUserProfileQuery =
    this.authenticationService.getAuthenticatedUserQuery({
      select: (response: any) => {
        const { status } = response ?? {};
        return status ? response : null;
      },
    });

  constructor() {
    /**
     * Effect watches loggedInUserProfileQuery.data() signal.
     * When /api/me returns, it:
     * 1. Extracts counts for header badges
     * 2. Caches user + sets role + permissions
     */
    effect(() => {
      const response: any = this.loggedInUserProfileQuery.data() ?? null;
      if (!response) return;

      const { data, counts } = response;
      if (counts) this.menuItemCounts.set(counts);

      const user: UserProfileType | null = data ?? null;
      if (!user) return;

      // Snapshot the permission set already in effect BEFORE re-applying, so we
      // can tell whether this /me response actually changed it.
      const previousPermissions = this.userPermissionService.getCurrentPermissions();
      this.userPermissionService.setPermissionsFromUser(user);

      // FEC-759: an admin can edit a role's permissions (permissions matrix)
      // while its users are logged in. The freshly-synced set is applied live
      // above, but route guards (e.g. dashboardAccessGuard) only re-run on
      // navigation — so prompt the user to log in again for the change to take
      // full effect. Skipped on the initial load (no previous set yet).
      if (previousPermissions.length > 0) {
        const nextPermissions = this.userPermissionService.getCurrentPermissions();
        const permissionsChanged =
          [...previousPermissions].sort().join('|') !== [...nextPermissions].sort().join('|');
        if (permissionsChanged) {
          this.toastService.success(
            'Your access permissions have changed. Please log in again to apply the updates.',
            6000,
            'bottom-center',
            'Permissions updated',
          );
        }
      }
    });

    /**
     * FEC-1173: re-sync the badge counts on every in-app navigation.
     *
     * The header lives in VerticalLayout, so it is constructed once per session
     * and its `/me` query never re-runs on its own — the counts therefore froze
     * at whatever they were on login and only moved after a hard page reload.
     * A route change is the natural, cheap moment to re-read them, and it also
     * picks up counts moved by *other* users, which no local invalidation can.
     *
     * The boot navigation is skipped: the initial `/me` request is still in
     * flight when its NavigationEnd fires, so invalidating there would just
     * duplicate the request the header already made.
     */
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.loggedInUserProfileQuery.isFetching()) return;
        this.authenticationService.invalidateAuthenticatedUserQuery();
      });
  }

  /**
   * Dropdown menu items for user avatar.
   *
   * "Settings" sits above Logout. It used to jump straight at the FEC-759
   * permissions matrix; since FEC-1174 Settings is an area with its own left
   * sub-menu (Roles and Permissions · Delete History · Import History ·
   * General), so it points at the shell instead. Developer Viewer is included
   * because it can read Delete History and Import History even though it cannot
   * edit the role matrix — `SETTINGS_ROUTES` and the sub-menu handle that.
   */
  protected readonly menuItems = computed<DropdownMenuItemType[]>(() => {
    const canOpenSettings =
      this.userPermissionService.isUserDeveloper() ||
      this.userPermissionService.isUserMasterAgent() ||
      this.userPermissionService.isUserDeveloperViewer();

    return [
      {
        label: 'My Account',
        routerLink: '/my-preferences',
      },
      ...(canOpenSettings
        ? [{ label: 'Settings', routerLink: '/settings' } as DropdownMenuItemType]
        : []),
      {
        label: 'Logout',
        command: () => {
          this.userPermissionService.clearAllPermissions();
          this.userPermissionService.clearUser();
          this.tokenService.removeToken();
          // Wipe in-memory + persisted query cache so a stale /me entry doesn't
          // get auto-refetched after logout (would 401 and trigger a refresh storm).
          this.authenticationService.clearQueryCache();
          this.router.navigate(['/auth/login']);
        },
      },
    ];
  });

  /**
   * User avatar data derived from the cached user in UserPermissionService.
   * Uses /api/me response fields (snake_case).
   */
  protected readonly userList = computed((): AvatarUserType[] => {
    const user = this.userPermissionService.user();
    if (!user) return [];

    return [
      {
        id: user.id,
        name: user.display_name || `${user.first_name} ${user.last_name}`,
        image: user.profile_image ? this.apiService.IMAGE_BASE_URL + user.profile_image : '',
      },
    ];
  });

  public ngOnInit(): void {
    this.handleResize();
    this.handleScroll();
  }

  /**
   * Navigation menu with counts from API.
   * Nav keys match API count keys: projects, reservations, project_requests, companies, users
   *
   * The Users badge counts pending user requests awaiting approval. An Agency
   * Manager cannot approve or reject them, so the badge is meaningless noise for
   * that role and is suppressed.
   */
  protected readonly userMenuList = computed((): any[] => {
    const counts = this.menuItemCounts();
    const hideUserCount = this.userPermissionService.isUserAgencyManager();

    return TopMenuNavigationList?.filter((item: SideNavigationMenuType) => {
      // Each entry already declared the capabilities it represents, but nothing
      // read them — the template filtered on `*hasRole` alone, so revoking (say)
      // VIEW_USER from an Agency Manager left the Users tab in the bar
      // (FEC-1160 QA, 21 Aug). An empty list means "no capability required"
      // (Sales Pipeline), and holding ANY listed slug is enough to show the tab.
      // Developer / Master Agent hold every permission, so they are unaffected.
      const required = item.permissions ?? [];
      return (
        required.length === 0 ||
        required.some((permission) => this.userPermissionService.hasPermission(permission))
      );
    }).map((item: SideNavigationMenuType) => ({
      ...item,
      count: hideUserCount && item.key === 'user' ? 0 : ((counts as any)[item.key ?? ''] ?? 0),
    }));
  });

  /** Current route URL as signal */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /** Active menu item that supports "Add New" action */
  protected readonly activeActionItem = computed(() => {
    const url = this.currentUrl() || '/';
    const menus = this.userMenuList() || [];

    const activeItem = menus.find((item: SideNavigationMenuType) => {
      const data: string =
        (item.permissions || []).find((permission) =>
          this.userPermissionService.hasPermission(
            `CREATE_${permission?.split('_')?.at(-1) ?? ''}`,
          ),
        ) || '';
      if (!(data ?? '').toLowerCase().includes('create')) return false;
      return item.isExact
        ? item.urlIncludes?.some((urlInclude) => url.includes(urlInclude)) || url === item.link
        : url.startsWith(item.link ?? '');
    });

    // FEC-1256: Agency Managers get no Add Project, even if the catalogue grants CREATE_PROJECT.
    if (activeItem?.key === 'project' && this.userPermissionService.isUserAgencyManager()) {
      return null;
    }

    if (activeItem) {
      const addLink = activeItem.key === 'project' ? '/project/add' : `${activeItem.link}/add`;
      return { ...activeItem, addLink };
    }
    return null;
  });

  /**
   * Whether the responsive menu shows its "add a project" shortcut (FEC-1150).
   * The header's own + is desktop-only now, so this is the only way to reach
   * Add Project on a phone — gated on the same permission the header used.
   */
  protected readonly canAddProject = computed<boolean>(() => {
    // `hasPermission` reads ngx-permissions, which is not a signal — depending
    // on the cached user is what makes this recompute once `/me` lands and the
    // permission set is applied.
    if (!this.userPermissionService.user()) return false;
    if (this.userPermissionService.isUserAgencyManager()) return false;
    return this.userPermissionService.hasPermission(ALL_PERMISSIONS.CREATE_PROJECT);
  });

  /** Checks if a navigation item matches the current URL */
  protected isLinkActive(item: any): boolean {
    const url = this.currentUrl() || '/';
    const link = item.link || '';
    return item.isExact ? url === link : url === link || url.startsWith(link + '/');
  }

  /** Mobile menu open/close state */
  protected readonly isMenuOpen = signal(false);

  /**
   * Whether the Projects row in the responsive menu is expanded (FEC-1150).
   * The project navigator renders inline underneath it rather than in a popover.
   */
  protected readonly isProjectsPanelOpen = signal(false);

  /** Toggles mobile menu and body scroll */
  protected toggleMenu(): void {
    this.isMenuOpen.update((v) => !v);
    // Closing the sheet also collapses the Projects panel, so re-opening the
    // menu always starts from the plain list of destinations.
    if (!this.isMenuOpen()) this.isProjectsPanelOpen.set(false);
    document.body.style.overflow = this.isMenuOpen() ? 'hidden' : 'auto';
  }

  /** Closes the responsive menu — used after navigating to a destination. */
  protected closeMenu(): void {
    if (this.isMenuOpen()) this.toggleMenu();
  }

  /** Expands / collapses the inline project navigator. */
  protected toggleProjectsPanel(): void {
    this.isProjectsPanelOpen.update((v) => !v);
  }

  /** Window resize handler — bound via host object */
  protected onResize(): void {
    this.handleResize();
  }
  /** Window scroll handler */
  protected onScroll(): void {
    this.handleScroll();
  }
  /** Handles scroll — toggle sticky bg based on scroll position */
  private handleScroll(): void {
    const shouldStick = window.scrollY >= 50;
    if (shouldStick !== this.isSticky()) {
      this.isSticky.set(shouldStick);
    }
  }
  /** Handles window resize logic — close mobile menu on desktop, manage sticky state */
  private handleResize(): void {
    // The responsive menu is the only navigation below `lg` (1024px) — the
    // desktop bar takes over from there, so that is where the sheet closes.
    if (window.innerWidth >= 1024 && this.isMenuOpen()) {
      this.isMenuOpen.set(false);
      this.isProjectsPanelOpen.set(false);
      document.body.style.overflow = 'auto';
    }
    const scrollPosition = window.scrollY;
    const shouldStick = scrollPosition >= 50;

    if (shouldStick !== this.isSticky()) {
      this.isSticky.set(shouldStick);
    }
  }
}
